import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { CookieConsentService } from "../../service/cookie-consent.service";

// How far the mobile settings sheet must be dragged down before it closes
const SWIPE_CLOSE_PX = 80;
// How often the mobile toast re-checks for fixed bottom bars (they change per step)
const BOTTOM_BAR_CHECK_MS = 500;
const MOBILE_MAX_PX = 768;

/**
 * MTR-1017: the consent toast (desktop: top right under the header, mobile: bottom
 * sheet sitting above any fixed bottom bar) and the Cookie Settings modal opened from
 * the footer.
 */
@Component({
  selector: "app-cookie-consent",
  templateUrl: "./cookie-consent.component.html",
  styleUrls: ["./cookie-consent.component.scss"]
})
export class CookieConsentComponent implements OnDestroy {
  @ViewChild("toast") toastRef?: ElementRef<HTMLElement>;

  // Pawsper-with-cookie illustration, exported from the Consumer Website Figma
  mascotSrc = "./assets/image/pawsper-cookie.png";

  // Draft toggle — only applied on Save, so closing the modal keeps the current choice
  analyticsOn = false;
  dragOffset = 0;
  // Keeps the mobile toast clear of the "Proceed"/total bars at the bottom of the screen
  toastBottom = 0;
  private dragStartY: number | null = null;
  private bottomBarTimer: number;
  private settingsOpened: Subscription;

  constructor(
    public consent: CookieConsentService,
    private translate: TranslateService,
    private zone: NgZone
  ) {
    this.settingsOpened = this.consent.settingsOpened.subscribe(() => {
      this.analyticsOn = this.consent.status === "accepted";
      this.dragOffset = 0;
    });

    // Outside Angular so the polling doesn't re-render the whole comparison page
    this.zone.runOutsideAngular(() => {
      this.bottomBarTimer = window.setInterval(
        () => this.updateToastBottom(),
        BOTTOM_BAR_CHECK_MS
      );
    });
  }

  get policyUrl(): string {
    const lang = this.translate.currentLang === "bm" ? "bm" : "en";
    return `${environment.mainsiteUrl}/${lang}/privacy-policy#cookie-policy`;
  }

  accept() {
    this.consent.save("accepted");
  }

  decline() {
    this.consent.save("declined");
  }

  toggleAnalytics() {
    this.analyticsOn = !this.analyticsOn;
  }

  saveSettings() {
    this.consent.save(this.analyticsOn ? "accepted" : "declined");
    this.consent.closeSettings();
  }

  closeSettings() {
    this.consent.closeSettings();
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    if (this.consent.showSettings) this.closeSettings();
  }

  onDragStart(event: TouchEvent) {
    this.dragStartY = event.touches[0].clientY;
  }

  onDragMove(event: TouchEvent) {
    if (this.dragStartY === null) return;
    // Only follow the finger downwards
    this.dragOffset = Math.max(0, event.touches[0].clientY - this.dragStartY);
  }

  onDragEnd() {
    if (this.dragStartY === null) return;
    this.dragStartY = null;

    if (this.dragOffset > SWIPE_CLOSE_PX) this.closeSettings();
    else this.dragOffset = 0;
  }

  /**
   * Finds whatever is fixed to the bottom of the screen (mobile bottombar, progress
   * sheet, sticky total) and lifts the toast above it, so the main CTA stays visible.
   */
  private updateToastBottom() {
    const toast = this.toastRef?.nativeElement;
    if (!toast || this.consent.showSettings) return;

    let bottom = 0;
    if (window.innerWidth <= MOBILE_MAX_PX) {
      const viewport = window.innerHeight;
      const probe = document.elementsFromPoint(window.innerWidth / 2, viewport - 2);

      for (const hit of probe) {
        if (toast.contains(hit)) continue;

        // Walk up to the element that is actually pinned to the viewport
        for (let el: Element | null = hit; el && el !== document.body; el = el.parentElement) {
          if (getComputedStyle(el).position !== "fixed") continue;

          const rect = el.getBoundingClientRect();
          // Full-screen layers (dialogs, spinners) are not bottom bars
          if (rect.height < viewport / 2) bottom = Math.max(bottom, viewport - rect.top);
          break;
        }
      }
    }

    bottom = Math.max(0, Math.round(bottom));
    if (bottom !== this.toastBottom) {
      this.zone.run(() => (this.toastBottom = bottom));
    }
  }

  ngOnDestroy() {
    window.clearInterval(this.bottomBarTimer);
    this.settingsOpened.unsubscribe();
  }
}
